import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { fmtDuration, shortId } from '@/lib/format';
import { Overview } from '@/pages/Overview';
import { Agents } from '@/pages/Agents';
import { AgentDetail } from '@/pages/AgentDetail';
import { Sessions } from '@/pages/Sessions';
import { NewSession } from '@/pages/NewSession';
import { SessionDetail } from '@/pages/SessionDetail';
import { CompareSessions } from '@/pages/CompareSessions';
import { Regression } from '@/pages/Regression';
import { Templates } from '@/pages/Templates';
import { Schedules } from '@/pages/Schedules';
import { ApiKeys } from '@/pages/ApiKeys';
import { Users } from '@/pages/Users';
import { Organizations } from '@/pages/Organizations';
import { Platform } from '@/pages/Platform';
import { Account } from '@/pages/Account';
import { EmptyState } from '@/components/common/States';
import { isAdmin, isSystemAdmin } from '@/types';

const NAV = [
  { to: '/', label: 'Overview', glyph: '◈', end: true, admin: false, sysAdmin: false },
  { to: '/agents', label: 'Agents', glyph: '⬡', end: false, admin: false, sysAdmin: false },
  { to: '/sessions', label: 'Sessions', glyph: '≋', end: false, admin: false, sysAdmin: false },
  { to: '/templates', label: 'Templates', glyph: '❑', end: false, admin: false, sysAdmin: false },
  { to: '/schedules', label: 'Schedules', glyph: '◷', end: false, admin: false, sysAdmin: false },
  { to: '/regression', label: 'Regression', glyph: '↗', end: false, admin: false, sysAdmin: false },
  { to: '/api-keys', label: 'API keys', glyph: '⚿', end: false, admin: false, sysAdmin: false },
  { to: '/users', label: 'Users', glyph: '◢', end: false, admin: true, sysAdmin: false },
  { to: '/platform', label: 'Platform', glyph: '▚', end: false, admin: true, sysAdmin: true },
  { to: '/organizations', label: 'Organizations', glyph: '⌂', end: false, admin: true, sysAdmin: true },
  { to: '/account', label: 'Account', glyph: '⛭', end: false, admin: false, sysAdmin: false },
];

function crumb(pathname: string): string[] {
  const seg = pathname.split('/').filter(Boolean);
  if (seg.length === 0) return ['Overview'];
  const head = seg[0][0].toUpperCase() + seg[0].slice(1);
  if (seg.length === 1) return [head];
  const tail = seg[1] === 'new' ? 'New test' : shortId(seg[1]);
  return [head, tail];
}

function Topbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const parts = crumb(loc.pathname);

  return (
    <header className="topbar">
      <div className="crumb">
        {parts.map((p, i) => (
          <span key={i}>
            {i > 0 && <span className="sep">/</span>}
            <span className={i < parts.length - 1 ? 'muted' : ''}>{p}</span>
          </span>
        ))}
      </div>
      <div className="row" style={{ gap: 12 }}>
        <div className="user-chip">
          <div className="who">
            <span className="em">{user?.email}</span>
            <span className="ro">{user?.role}</span>
          </div>
          <span className="bal" title="traffic balance">{fmtDuration(user?.traffic_seconds_balance)}</span>
        </div>
        <button
          className="btn btn-ghost"
          onClick={async () => {
            await logout();
            nav('/login', { replace: true });
          }}
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

export function AppShell() {
  const { user } = useAuth();

  return (
    <div style={{ display: 'flex', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      <aside
        style={{
          width: 'var(--sidebar)',
          flexShrink: 0,
          borderRight: '1px solid var(--line)',
          padding: '22px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          background: 'linear-gradient(180deg, rgba(15,21,33,0.6), transparent)',
        }}
      >
        <div style={{ padding: '4px 8px 22px' }}>
          <div className="eyebrow" style={{ fontSize: 10 }}>Arc</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, marginTop: 4 }}>
            <span className="signal">Z</span>EPHLON
          </div>
        </div>

        <nav className="stack" style={{ gap: 4 }}>
          {NAV.filter((n) => (!n.admin || isAdmin(user)) && (!n.sysAdmin || isSystemAdmin(user))).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className="navlink">
              <span style={{ width: 18, textAlign: 'center', opacity: 0.8 }}>{n.glyph}</span>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="grow" />

        <div className="eyebrow" style={{ fontSize: 9, padding: '0 10px', opacity: 0.6 }}>
          control console · v0.1
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Topbar />
        <main style={{ flex: 1, minWidth: 0, padding: '28px 32px 64px' }}>
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/agents/:id" element={<AgentDetail />} />
            <Route path="/sessions" element={<Sessions />} />
            <Route path="/sessions/new" element={<NewSession />} />
            <Route path="/sessions/compare" element={<CompareSessions />} />
            <Route path="/regression" element={<Regression />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/schedules" element={<Schedules />} />
            <Route path="/api-keys" element={<ApiKeys />} />
            <Route path="/sessions/:id" element={<SessionDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/account" element={<Account />} />
            <Route
              path="*"
              element={<EmptyState title="Not found" hint="That route doesn't exist." />}
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}
